import {GameActions, IGameState, ILevelMapProgress} from "./types";
import {Reducer} from "redux";
import {getType} from "typesafe-actions";
import {SceneryType} from "../settings/SceneryType";
import * as actions from "./actions";
import {updateDailyStreak} from "./actions";

export const gameInitialState: IGameState = {
    levelInProgress: 0,
    currentTileId: 0,
    levelScenery: SceneryType.Summer,
    categories: Object.create(null),
    categoriesFetchTime: 0,
    questions: Object.create(null),
    unusedQuestions: [],
    unusedQuestionsFetchLanguage: "English",
    answers: Object.create(null),
    authors: Object.create(null),
    levelMaps: Object.create(null),
    levelMapProgress: Object.create(null),
    levelMapTileProgress: Object.create(null),
    dailyStreakLastTimeProcessed: new Date().getTime(),
    dailyStreak: -1,
};

export const gameReducer : Reducer<IGameState, GameActions> = (state = gameInitialState, action): IGameState => {
    switch (action.type) {
        case getType(actions.startLevel):
            return {
                ...state,
                levelInProgress: action.payload.level,
                levelScenery: action.payload.scenery,
                levelMapProgress: {
                    ...state.levelMapProgress,

                    [action.payload.level]: {
                        id: action.payload.level,
                        startLevelTime: new Date().getTime(),
                        scoreMultiplier: 1,
                        appliedHints: [],
                        activeHints: [],
                        isExitFound: false,
                        allTilesAreRevealed: false,
                        isCompleted: false,
                        answeredTileCount: 0,
                        answeredQuestions: 0,
                        totalQuestions: 0,
                        totalScore: 0,
                        completionPercent: 0,
                        rightAnswersPercent: 0,
                        completeLevelTime: 0,
                        stars: 0,

                        ...(state.levelMapProgress[action.payload.level] as Partial<ILevelMapProgress>)
                    }
                }
            };

        case getType(actions.completeLevel):
            return {
                ...state,
                levelMapProgress: {
                    ...state.levelMapProgress,
                    [action.payload.id]: {
                        ...state.levelMapProgress[action.payload.id],
                        ...action.payload,
                        completeLevelTime: new Date().getTime(),
                    }
                }
            };

        case getType(actions.openTile):
            return {
                ...state,
                currentTileId: action.payload.id
            };

        case getType(actions.incrementScoreMultiplier):
            return {
                ...state,
                levelMapProgress: {
                    ...state.levelMapProgress,
                    [state.levelInProgress]: {
                        ...state.levelMapProgress[state.levelInProgress],

                        scoreMultiplier: state.levelMapProgress[state.levelInProgress].scoreMultiplier + 0.1
                    }
                },
            };

        case getType(actions.replaceQuestion):
            return {
                ...state,
                levelMapTileProgress: {
                    ...state.levelMapTileProgress,
                    [state.currentTileId]: {
                        ...state.levelMapTileProgress[state.currentTileId],

                        overriddenQuestions: {
                            [action.payload.current]: action.payload.replaceWith
                        }
                    }
                }
            };

        case getType(actions.setQuestion):
            const questionIndex = state.unusedQuestions.indexOf(action.payload);
            if (questionIndex < 0)
                return state;

            return {
                ...state,
                unusedQuestions: [
                    ...state.unusedQuestions.slice(0, questionIndex),
                    ...state.unusedQuestions.slice(questionIndex + 1)
                ]
            };

        case getType(actions.setLevelProgress):
            const levelMapTileProgress = {
                ...state.levelMapTileProgress
            };

            let answeredTileCount = 0;

            Object.values(action.payload.levelProgress).forEach(tileProgress => {
                levelMapTileProgress[tileProgress.id] = {
                    ...state.levelMapTileProgress[tileProgress.id],
                    ...tileProgress
                };

                if (tileProgress.isAnswered) {
                    answeredTileCount++;
                }
            });

            return {
                ...state,
                levelMapTileProgress,

                levelMapProgress: {
                    ...state.levelMapProgress,

                    [state.levelInProgress]: {
                        ...state.levelMapProgress[state.levelInProgress],

                        isExitFound: state.levelMapProgress[state.levelInProgress].isExitFound || action.payload.isExitFound|| false,
                        isCompleted: state.levelMapProgress[state.levelInProgress].isCompleted || action.payload.isCompleted|| false,
                        allTilesAreRevealed: state.levelMapProgress[state.levelInProgress].allTilesAreRevealed || action.payload.allTilesAreRevealed|| false,
                        answeredTileCount: state.levelMapProgress[state.levelInProgress].answeredTileCount + answeredTileCount
                    }
                },
            };

        case getType(actions.fetchLevelMap.success):
            return {
                ...state,
                levelMaps: {
                    ...state.levelMaps,
                    ...action.payload.levelMaps
                },
                questions: {
                    ...state.questions,
                    ...action.payload.questions
                },
                answers: {
                    ...state.answers,
                    ...action.payload.answers
                },
                authors: {
                    ...state.authors,
                    ...action.payload.authors
                }
            };

        case getType(actions.fetchCategories.success):
            return {
                ...state,
                categories: action.payload,
                categoriesFetchTime: new Date().getTime()
            };

        case getType(actions.fetchQuestions.success):
            return {
                ...state,
                questions: {
                    ...state.questions,
                    ...action.payload.questions
                },
                answers: {
                    ...state.answers,
                    ...action.payload.answers
                },
                authors: {
                    ...state.authors,
                    ...action.payload.authors
                },
                unusedQuestions: action.payload.languageChanged
                    ? action.payload.unusedQuestions || []
                    : [...state.unusedQuestions, ...(action.payload.unusedQuestions || [])],
                unusedQuestionsFetchLanguage: action.payload.unusedQuestionsFetchLanguage || state.unusedQuestionsFetchLanguage
            };

        case getType(updateDailyStreak):
            return {
                ...state,
                dailyStreakLastTimeProcessed: new Date().getTime(),
                dailyStreak: action.payload,
            };

        default:
            return state;
    }
};
